'use client';

// lib/clients/ClientContext.tsx
// React context for providing ClientConfig to frontend components.
// Receives serialized config from server, hydrates Set/Function on client side.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ClientConfig } from './types';
import type { SerializedClientConfig } from './serialize';

const ClientContext = createContext<ClientConfig | null>(null);

/**
 * Hydrate a serialized config back to a full ClientConfig.
 * Restores Set objects and provides default client-side functions.
 */
function hydrateConfig(s: SerializedClientConfig): ClientConfig {
  return {
    ...s,
    facetFields: s.facetFields.map(f => ({
      ...f,
      // skipStems not available on client — only used server-side in vocabulary extraction
    })),
    language: {
      ...s.language,
      // RegExp patterns not used on client side — search runs server-side
      pricePatterns: { maxPrice: /(?:)/, minPrice: /(?:)/ },
      noisyPhrases: [],
    },
    systemPrompt: {
      ...s.systemPrompt,
      // buildPrompt not used on client side — agent chat calls server API
      buildPrompt: () => '',
    },
    ui: {
      ...s.ui,
      categoriesToHide: new Set(s.ui.categoriesToHide),
      strings: {
        ...s.ui.strings,
        showMore: (n: number) => s.ui.strings.showMoreTemplate.replace('{{n}}', String(n)),
        aiAssistantTitle: s.ui.strings.aiAssistantTitle || 'AI Βοηθός',
        aiAssistantGreeting: s.ui.strings.aiAssistantGreeting || 'Πώς μπορώ να σε βοηθήσω; Ρώτησέ με για προϊόντα, δώρα ή οτιδήποτε χρειάζεσαι.',
      },
    },
  };
}

interface ClientProviderProps {
  children: ReactNode;
  serializedConfig: SerializedClientConfig;
}

export function ClientProvider({ children, serializedConfig }: ClientProviderProps) {
  const config = useMemo(() => hydrateConfig(serializedConfig), [serializedConfig]);
  return (
    <ClientContext.Provider value={config}>
      {children}
    </ClientContext.Provider>
  );
}

/**
 * Access the active client configuration from any client component.
 * Must be used within a <ClientProvider>.
 */
export function useClientConfig(): ClientConfig {
  const config = useContext(ClientContext);
  if (!config) {
    throw new Error('useClientConfig must be used within a <ClientProvider>');
  }
  return config;
}
